"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";

import useCountries from "@/app/hooks/useCountries";
import { SafeDiveCenter, SafeListing, SafeReservation, SafeUser } from "@/app/types";

import HeartButton from "../HeartButton";
import Button from "../Button";
import useCurrencies from "@/app/hooks/useCurrencies";
import useCurrencyStore from "@/app/hooks/useCurrencyStore";
import { ExchangeCurrency } from "@prisma/client";
import RatingStar from "../RatingStar";
import convertPrice from "@/app/utils/CurrencyConverter";
import formatPrice from "@/app/utils/formatPrice";
import {
  CERTIFICATION_BADGE_BG,
  getCategoryColorRgba,
} from "@/app/utils/categoryColors";
import Link from "next/link";
import CertificationBadge from "./CertificationBadge";

/** First certification agency for badge (e.g. "PADI, SSI" → "PADI") */
function primaryAgencyLabel(agency: string | null | undefined): string | null {
  if (!agency?.trim()) return null;
  return agency.split(",")[0]?.trim() || null;
}

interface ListingCardProps {
  listing: SafeListing;
  reservation?: SafeReservation;
  onEdit?: (id: string) => void;
  onEditDates?: (id: string) => void;
  onAction?: (id: string) => void;
  disabled?: boolean;
  editing?: boolean;
  editingDates?: boolean;
  actionLabel?: string;
  editLabel?: string;
  editDatesLabel?: string;
  actionId?: string;
  currentUser?: SafeUser | null;
  diveCenter?: SafeDiveCenter | null;
  rates: ExchangeCurrency | null;
}

const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  reservation,
  onEdit,
  onEditDates,
  onAction,
  disabled,
  editing,
  editingDates,
  actionLabel,
  editLabel,
  editDatesLabel,
  actionId = "",
  currentUser,
  diveCenter,
  rates,
}) => {
  const router = useRouter();
  const [convertedPrice, setConvertedPrice] = useState<number | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { getByName } = useCountries();

  const location = getByName(listing.country || "");
  const { getByCode } = useCurrencies();
  const currencyStore = useCurrencyStore();
  const selectedCurrency = currencyStore.selectedCurrency;
  const currentCode = getByCode(selectedCurrency?.code || currentUser?.currency || "USD");
  const newCurrencySymbol = currentCode?.symbol || "$";

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) {
        return;
      }

      onAction?.(actionId);
    },
    [disabled, onAction, actionId]
  );

  const price = useMemo(() => {
    if (reservation) {
      return reservation.totalPrice;
    }

    return listing.price;
  }, [reservation, listing.price]);

  const diveCenterName = useMemo(() => {
    return listing.diveCenter.user.name;
  }, [listing.diveCenter]);

  const agencyBadge = primaryAgencyLabel(listing.diveCenter?.agency);

  const durationLabel = useMemo(() => {
    if (!listing.length || listing.length <= 0) return null;
    const unit = (listing.lengthUnit || "days").toLowerCase();
    const singular = unit.endsWith("s") ? unit.slice(0, -1) : unit;
    return `${listing.length} ${listing.length === 1 ? singular : unit}`;
  }, [listing.length, listing.lengthUnit]);

  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (editing) {
        return;
      }

      onEdit?.(actionId);
    },
    [editing, onEdit, actionId]
  );

  const handleEditDates = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (editingDates) {
        return;
      }

      onEditDates?.(actionId);
    },
    [editingDates, onEditDates, actionId]
  );

  useEffect(() => {
    if (!currentUser) {
      setConvertedPrice(price);
    }
  }, [price, currentUser])

  useEffect(() => {
    let baseCurrency = selectedCurrency?.code || currentUser?.currency;

    if (!baseCurrency) {
      baseCurrency = "USD";
    }

    if (baseCurrency === "USD" && listing.currency === "USD") {
      setConvertedPrice(price);
      return;
    }

    const fetchConvertedPrice = async () => {
      const newPrice = await convertPrice(price, listing.currency || "", baseCurrency, rates);
      setConvertedPrice(parseFloat(newPrice.toFixed(0)));
    };

    fetchConvertedPrice();
  }, [rates, currentUser, price, selectedCurrency, listing.currency]);

  const listingData = {
    title: listing?.title,
    price: listing?.price,
    currency: listing?.currency,
    category: listing?.category,
    city: listing?.city,
    country: listing?.country,
  };
  const urlWithParams = `/listings/${listing?.id}?listingData=${encodeURIComponent(
    JSON.stringify(listingData)
  )}`;

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Allow Ctrl+Click, Cmd+Click, or middle click to open in new tab
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      return; // Let the browser handle it naturally
    }
    // Regular click - navigate normally
    if (e.button === 0) {
      e.preventDefault();
      router.push(urlWithParams);
    }
  }, [router, urlWithParams]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Allow right-click context menu to work normally
    // The Link component will handle opening in new tab
  }, []);

  return (
    <Link
      href={urlWithParams}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onAuxClick={(e) => {
        // Middle mouse button click - let browser handle (opens in new tab)
        if (e.button === 1) {
          return;
        }
      }}
      className="col-span-1 cursor-pointer group block"
    >
      <div className="flex flex-col w-full">
        {/* Imagen */}
        <div
          className="
            aspect-square 
            w-full 
            relative 
            overflow-hidden 
            rounded-xl
            bg-gray-200
            mb-3
          "
        >
          <Image
            src={listing.imageSrc || "/images/no-photo-yet.jpg"}
            alt={listing.title || "Dive listing"}
            loading="lazy"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 20vw"
            fill
            quality={60}
            className={`
              object-cover 
              transition
              ${imageLoaded ? 'opacity-100 group-hover:scale-110' : 'opacity-0'}
            `}
            onLoad={() => setImageLoaded(true)}
            placeholder="blur"
            blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2UyZThmMCIvPjwvc3ZnPg=="
          />
          {!currentUser || currentUser.userType === "Diver" ? (
            <div className="absolute top-3 right-3 z-10">
              <HeartButton listingId={listing.id} currentUser={currentUser} />
            </div>
          ) : null}

          {/* Certification + agency + category pills — bottom of image */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] flex flex-col items-start gap-1 bg-gradient-to-t from-black/55 via-black/20 to-transparent px-2 pb-2 pt-8">
            {/* Certification requirement — first thing a beginner must see */}
            <CertificationBadge
              minCertification={listing.minCertification}
              category={listing.category}
            />
            <div className="flex w-full flex-wrap items-end gap-1">
            {agencyBadge ? (
              <span
                className="max-w-[48%] truncate rounded-md px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm ring-1 ring-white/20"
                style={{ backgroundColor: CERTIFICATION_BADGE_BG }}
              >
                {agencyBadge}
              </span>
            ) : null}
            {listing.category ? (
              <span
                className="max-w-[48%] truncate rounded-md px-1.5 py-0.5 text-[10px] font-semibold capitalize text-white shadow-sm ring-1 ring-white/15"
                style={{
                  backgroundColor: getCategoryColorRgba(listing.category, 0.9),
                }}
              >
                {listing.category}
              </span>
            ) : null}
            </div>
          </div>
        </div>

        {/* Información del contenido */}
        <div className="space-y-1">
          {/* Centro de buceo y rating */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 capitalize truncate max-w-[70%] font-medium">
              {diveCenterName}
            </div>
            <RatingStar
              averageRating={
                (listing.diveCenter?.averageRating && listing.diveCenter.averageRating > 0)
                  ? listing.diveCenter.averageRating
                  : (listing.diveCenter?.tripAdvisorLocationId
                      ? (listing.diveCenter?.averageRatingTripAdvisor || 0)
                      : 0)
              }
              reviewsNumber={
                (listing.diveCenter?.averageRating && listing.diveCenter.averageRating > 0)
                  ? (listing.diveCenter?.totalReviews || 0)
                  : (listing.diveCenter?.tripAdvisorLocationId
                      ? (listing.diveCenter?.totalReviewsTripAdvisor || 0)
                      : 0)
              }
            />
          </div>

          {/* Título principal */}
          <div className="font-semibold text-base text-gray-900 capitalize truncate leading-tight">
            {listing.title}
          </div>

          {/* Ubicación + duración */}
          <div className="flex items-center justify-between gap-2 text-sm text-gray-700 font-medium">
            <span className="truncate">
              {listing?.city}, {location?.label}
            </span>
            {durationLabel && (
              <span className="shrink-0 font-normal text-gray-500">
                {durationLabel}
              </span>
            )}
          </div>

          {/* Precio */}
          <div className="flex flex-row items-baseline gap-1 pt-1">
            <div className="font-bold text-md text-gray-900">
              {((currentUser || !selectedCurrency?.symbol) ? newCurrencySymbol : selectedCurrency.symbol || "$")}
              {convertedPrice !== null ? formatPrice(convertedPrice) : '...'}
              {(newCurrencySymbol === "$" && selectedCurrency?.symbol === "$") && (
                <span className="font-normal text-xs text-gray-500 ml-1">
                  {selectedCurrency?.code || currentUser?.currency}
                </span>
              )}
            </div>
            {!reservation && (
              <div className="font-normal text-sm text-gray-500">/ diver</div>
            )}
          </div>

          {/* Botones de acción */}
          {(onEdit || onEditDates || onAction) && (
            <div className="flex flex-col gap-2 mt-2">
              {onEdit && (
                <Button
                  small
                  label={editLabel || ""}
                  onClick={handleEdit}
                />
              )}

              {onEditDates && (
                <Button
                  small
                  label={editDatesLabel || ""}
                  onClick={handleEditDates}
                />
              )}

              {onAction && actionLabel && (
                <Button
                  disabled={disabled}
                  small
                  label={actionLabel}
                  onClick={handleCancel}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ListingCard;